import { WorkloadRecommendationStatusTypeEnum } from '@cast/types';

import { PreparedWorkload } from 'features/workload-optimization';

import { AccessOption, OnOffOption, OptimizedOption } from './_types';
import { ERROR_STATUS_TYPE, LOW_CONFIDENCE_STATUS_TYPE } from '../_constants';
import type { FilterableStatuses } from '../_types';
import {
  isWorkloadOptimized,
  isPredictiveWorkloadEligible,
  hpaExists,
} from '../_utils';
import {
  hasError,
  hasLowConfidence,
  isAppliedWithPods,
  isStopped,
} from '../_workloadStatusUtils';

import { ClientSideSearchCriterion } from '@cast/shared-search-ui';

export const predictiveCriterion: ClientSideSearchCriterion<PreparedWorkload> =
  {
    key: 'predictive',
    getValue(option) {
      return isPredictiveWorkloadEligible(option) ? 'true' : 'false';
    },
  };

export const configuredViaCriterion: ClientSideSearchCriterion<PreparedWorkload> =
  {
    key: 'managedBy',
    prop: 'managedBy',
  };

export const verticalScalingPolicyCriterion: ClientSideSearchCriterion<PreparedWorkload> =
  {
    key: 'verticalScalingPolicy',
    getValue: ({ verticalScalingPolicy }) => verticalScalingPolicy?.name,
    matcher: 'exact',
  };

export const statusCriterion: ClientSideSearchCriterion<
  PreparedWorkload,
  FilterableStatuses[]
> = {
  key: 'status',
  matcher: (option, value) => {
    if (!value?.length) {
      return false;
    }

    if (value.includes(WorkloadRecommendationStatusTypeEnum.APPLIED)) {
      return isAppliedWithPods(option);
    }

    if (value.includes(WorkloadRecommendationStatusTypeEnum.STOPPED)) {
      return isStopped(option);
    }

    if (value.includes(LOW_CONFIDENCE_STATUS_TYPE)) {
      return hasLowConfidence(option);
    }

    if (value.includes(ERROR_STATUS_TYPE)) {
      return hasError(option);
    }

    return false;
  },
};

export const optimizedCriterion: ClientSideSearchCriterion<PreparedWorkload> = {
  key: 'optimized',
  getValue: ({ isVerticalScalingEnabled, isHorizontalScalingEnabled }) => ({
    isVerticalScalingEnabled,
    isHorizontalScalingEnabled,
  }),
  matcher: (option, value?: OptimizedOption[]) => {
    if (!value?.length) {
      return false;
    }

    const hasNone = value.includes(OptimizedOption.NONE);
    const hasOptimized = value.includes(OptimizedOption.OPTIMIZED);

    if (hasNone && hasOptimized) {
      return true;
    }

    if (hasNone) {
      return !isWorkloadOptimized(option);
    }

    if (hasOptimized) {
      return isWorkloadOptimized(option);
    }

    return false;
  },
};

export const hpaCriterion: ClientSideSearchCriterion<PreparedWorkload> = {
  key: 'hpa',
  prop: 'isHorizontalScalingEnabled',
  matcher: 'exact',
};

export const jvmCriterion: ClientSideSearchCriterion<PreparedWorkload> = {
  key: 'jvm',
  prop: 'isJvmWorkload',
  matcher: 'exact',
};

export const verticalOptimizationCriterion: ClientSideSearchCriterion<PreparedWorkload> =
  {
    key: 'verticalOptimization',
    getValue: ({ isVerticalScalingEnabled }) =>
      isVerticalScalingEnabled ? OnOffOption.ON : OnOffOption.OFF,
    matcher: 'exact',
  };

export const horizontalOptimizationCriterion: ClientSideSearchCriterion<PreparedWorkload> =
  {
    key: 'horizontalOptimization',
    matcher: (workload, value?: OnOffOption[]) => {
      if (!value?.length) {
        return false;
      }
      if (!workload.hpaState || !hpaExists(workload.hpaState)) {
        return false;
      }
      const state = workload.isHorizontalScalingEnabled
        ? OnOffOption.ON
        : OnOffOption.OFF;
      return value.includes(state);
    },
  };

export const makeAccessCriterion = (
  isNamespaceWritable: (namespace: string | undefined) => boolean
): ClientSideSearchCriterion<PreparedWorkload, AccessOption> => ({
  key: 'access',
  matcher: (workload, value?: AccessOption) => {
    if (!value || value === AccessOption.ALL) {
      return true;
    }
    const isWritable = isNamespaceWritable(workload.namespace);
    return value === AccessOption.RESTRICTED ? !isWritable : isWritable;
  },
});
